import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { LifeBuoy } from "lucide-react";

export default function HelpDesk() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [form, setForm] = useState({ category: "Software Issue", subject: "", description: "" });

  const load = () => {
    if (user && typeof user === "object") api.get("/tickets/mine").then(r => setTickets(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user || typeof user !== "object") { toast.error("Login required"); return; }
    try {
      await api.post("/tickets", form);
      toast.success("Ticket submitted");
      setForm({ category: "Software Issue", subject: "", description: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-brand-orange font-semibold flex items-center gap-1.5"><LifeBuoy className="w-3.5 h-3.5" /> Help Desk</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Raise a technical ticket</h1>
            <p className="text-muted-foreground mt-2">Get support from the KELTA technical team.</p>
          </div>

          {(!user || typeof user !== "object") ? (
            <Card className="p-8 text-center">
              <p className="text-muted-foreground">Please <Link to="/login" className="text-brand-blue font-medium">login</Link> to raise a ticket.</p>
            </Card>
          ) : (
            <>
              <Card className="p-6">
                <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                      <SelectTrigger data-testid="help-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Website Issue", "Network Issue", "Software Issue", "Infrastructure Issue"].map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subject</Label>
                    <Input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} required data-testid="help-subject" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Description</Label>
                    <Textarea rows={5} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required data-testid="help-description" />
                  </div>
                  <div className="sm:col-span-2">
                    <Button type="submit" className="bg-brand-blue text-white hover:bg-brand-blue/90" data-testid="help-submit-btn">Submit Ticket</Button>
                  </div>
                </form>
              </Card>

              <h2 className="font-bold text-xl mt-10 mb-3">My Tickets</h2>
              <Card>
                <ul className="divide-y divide-border">
                  {tickets.length === 0 && <li className="p-6 text-center text-sm text-muted-foreground">No tickets yet.</li>}
                  {tickets.map(t => (
                    <li key={t.id} className="p-4 flex items-start justify-between gap-4">
                      <div>
                        <div className="font-semibold">{t.subject}</div>
                        <div className="text-xs text-muted-foreground">{t.category} • {new Date(t.created_at).toLocaleString()}</div>
                        <div className="text-sm mt-1">{t.description}</div>
                      </div>
                      <Badge variant={t.status === "Resolved" ? "default" : "secondary"}>{t.status}</Badge>
                    </li>
                  ))}
                </ul>
              </Card>
            </>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
